
import { useState } from 'react';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    investmentRange: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const teamMembers = [
    {
      name: 'Sande Dotyeni',
      position: 'Founder & CEO',
      company: 'TS Dotyeni International',
      location: 'South Africa Office (Sandton) · SAST (UTC+2)',
      description: 'Principal Developer for All Smart Village Investments',
      image: 'https://static.readdy.ai/image/46a311af9e0bece68b4ea3d810aabbdd/cf1eca2bc5df656d6786337d8824821d.jfif',
      email: 'sande@tsdotyeni.com',
      altEmail: 'sande@smartvillages.co.za',
      website: 'www.tsdotyeni.com'
    },
    {
      name: 'Henry Blackwood',
      position: 'Principal, Smart Villages Programme',
      company: 'TS Dotyeni International',
      location: 'France Office (Paris) · CET/CEST (UTC+1/UTC+2)',
      description: 'Strategic Programme Leadership',
      image: 'https://public.readdy.ai/ai/img_res/7ca870e09cac5e62fb884519d5479b42.jpg',
      email: 'henry.blackwood@tsdotyeni.com',
      website: 'www.smartvillages.co.za'
    },
    {
      name: 'James Ashbury',
      position: 'Project Management Office (PMO)',
      company: 'TS Dotyeni International',
      location: 'France Office (Paris) · CET/CEST (UTC+1/UTC+2)',
      description: 'Project Delivery & Operations',
      image: 'https://public.readdy.ai/ai/img_res/6275bb2cb880ab0c3d0fbd1f64763be0.jpg',
      email: 'james.ashbury@tsdotyeni.com',
      website: 'www.smartvillages.co.za'
    },
    {
      name: 'Charlotte Whitmore',
      position: 'Chief Development Officer',
      company: 'TS Dotyeni International',
      location: 'Singapore Office · SGT (UTC+8)',
      description: 'Strategic Development & Partnerships',
      image: 'https://readdy.ai/api/search-image?query=Professional%20executive%20portrait%20of%20Charlotte%20Whitmore%20in%20business%20attire%2C%20chief%20development%20officer%2C%20modern%20Singapore%20office%20background%2C%20confident%20female%20executive%20headshot%20photography&width=400&height=400&seq=charlotte-whitmore&orientation=squarish',
      email: 'charlotte.whitmore@tsdotyeni.com',
      website: 'www.smartvillages.co.za'
    },
    {
      name: 'Edward Langford',
      position: 'Programme Director, Smart Villages Delivery',
      company: 'TS Dotyeni International',
      location: 'Singapore Office · SGT (UTC+8)',
      description: 'Programme Implementation & Delivery',
      image: 'https://readdy.ai/api/search-image?query=Professional%20business%20portrait%20of%20Edward%20Langford%20in%20formal%20executive%20attire%2C%20programme%20director%2C%20modern%20corporate%20Singapore%20office%20setting%2C%20confident%20leadership%20headshot%20with%20professional%20lighting&width=400&height=400&seq=edward-langford&orientation=squarish',
      email: 'edward.langford@tsdotyeni.com',
      website: 'www.smartvillages.co.za'
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'message') {
      if (value.length <= 500) {
        setFormData(prev => ({ ...prev, [name]: value }));
        setCharCount(value.length);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.email || !formData.message) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const submitData = new URLSearchParams();
      submitData.append('fullName', formData.fullName);
      submitData.append('email', formData.email);
      submitData.append('company', formData.company);
      submitData.append('investmentRange', formData.investmentRange);
      submitData.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d43n3oumj2r470qadgb0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          company: '',
          investmentRange: '',
          message: ''
        });
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Team Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Meet Our <span className="text-emerald-600">Global Team</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Connect with the dedicated professionals behind TS Dotyeni International. .
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                <div className="text-center">
                  <div className="relative mb-6">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-32 h-32 rounded-full mx-auto object-cover object-top ring-4 ring-emerald-100 group-hover:ring-emerald-200 transition-all duration-300"
                    />
                    <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-white text-sm"></i>
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                    {member.name}
                  </h3>
                  
                  <p className="text-lg text-emerald-600 font-semibold mb-2">
                    {member.position}
                  </p>
                  
                  <p className="text-gray-700 font-medium mb-2">
                    {member.company}
                  </p>
                  
                  <p className="text-sm text-gray-500 mb-3">
                    {member.location}
                  </p>
                  
                  <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                    {member.description}
                  </p>
                  
                  <div className="space-y-3">
                    <a 
                      href={`mailto:${member.email}`}
                      className="flex items-center justify-center text-gray-700 hover:text-emerald-600 transition-colors group/link"
                    >
                      <i className="ri-mail-line mr-2 group-hover/link:scale-110 transition-transform"></i>
                      <span className="text-sm">{member.email}</span>
                    </a>
                    
                    {member.altEmail && (
                      <a 
                        href={`mailto:${member.altEmail}`}
                        className="flex items-center justify-center text-gray-700 hover:text-emerald-600 transition-colors group/link"
                      >
                        <i className="ri-mail-line mr-2 group-hover/link:scale-110 transition-transform"></i>
                        <span className="text-sm">{member.altEmail}</span>
                      </a>
                    )}
                    
                    <a 
                      href={`https://${member.website}`}
                      className="flex items-center justify-center text-gray-700 hover:text-emerald-600 transition-colors group/link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="ri-global-line mr-2 group-hover/link:scale-110 transition-transform"></i>
                      <span className="text-sm">{member.website}</span>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Form Section */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Contact Information */}
            <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 p-12 text-white">
              <h3 className="text-3xl font-bold mb-8">Get In Touch</h3>
              <p className="text-emerald-100 mb-8 text-lg leading-relaxed">
                Ready to explore investment opportunities in rural South Africa? Our team is here to guide you through the process.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center mr-4 mt-1">
                    <i className="ri-map-pin-line text-emerald-200"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Office Location</h4>
                    <p className="text-emerald-100">Sandton, Johannesburg<br />South Africa</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center mr-4 mt-1">
                    <i className="ri-phone-line text-emerald-200"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Phone</h4>
                    <p className="text-emerald-100">+27 (0) 11 234 5678</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center mr-4 mt-1">
                    <i className="ri-mail-line text-emerald-200"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Email</h4>
                    <p className="text-emerald-100">b2b@tsdotyeni.com</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center mr-4 mt-1">
                    <i className="ri-time-line text-emerald-200"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Business Hours</h4>
                    <p className="text-emerald-100">Monday - Friday: 9:00 AM - 5:00 PM<br />Saturday: 9:00 AM - 1:00 PM</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-12">
                <h4 className="font-semibold mb-4">Follow Our Journey</h4>
                <div className="flex space-x-4">
                  <a href="#" className="w-10 h-10 bg-emerald-500 hover:bg-emerald-400 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                    <i className="ri-linkedin-fill text-white"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-emerald-500 hover:bg-emerald-400 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                    <i className="ri-twitter-fill text-white"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-emerald-500 hover:bg-emerald-400 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                    <i className="ri-facebook-fill text-white"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="p-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-8">Start Your Investment Journey</h3>
              
              <form id="investment-inquiry-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-sm"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                    Company/Organization
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-sm"
                    placeholder="Your company name (optional)"
                  />
                </div>

                <div>
                  <label htmlFor="investmentRange" className="block text-sm font-semibold text-gray-700 mb-2">
                    Investment Range
                  </label>
                  <div className="relative">
                    <select
                      id="investmentRange"
                      name="investmentRange"
                      value={formData.investmentRange}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none bg-white cursor-pointer text-sm"
                    >
                      <option value="">Select investment range</option>
                      <option value="R100k-R500k">R100,000 - R500,000</option>
                      <option value="R500k-R1M">R500,000 - R1,000,000</option>
                      <option value="R1M-R5M">R1,000,000 - R5,000,000</option>
                      <option value="R5M+">R5,000,000+</option>
                      <option value="institutional">Institutional Investment</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none transition-all text-sm"
                    placeholder="Tell us about your investment interests and goals..."
                  ></textarea>
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-xs text-gray-500">
                      {charCount}/500 characters
                    </p>
                    {charCount > 450 && (
                      <p className="text-xs text-amber-600">
                        {500 - charCount} characters remaining
                      </p>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || charCount > 500}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 disabled:hover:scale-100 cursor-pointer whitespace-nowrap flex items-center justify-center text-sm"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Send Investment Inquiry
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center">
                    <i className="ri-check-line text-emerald-600 mr-3"></i>
                    <p className="text-emerald-700 text-sm">Thank you! Your inquiry has been sent successfully. We'll be in touch soon.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center">
                    <i className="ri-error-warning-line text-red-600 mr-3"></i>
                    <p className="text-red-700 text-sm">Sorry, there was an error sending your message. Please try again.</p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
